import React, { useState, useEffect, useCallback } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { getAuditLogs, exportAuditLogs, getUsers } from '../utils/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  FunnelIcon,
  ArrowDownTrayIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const AuditLogs = () => {
  usePageTitle('Audit Logs');

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [users, setUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    user_id: '',
    date_from: '',
    date_to: '',
  });
  const [exporting, setExporting] = useState(false);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: 50, ...filters };
      Object.keys(params).forEach((key) => { if (!params[key]) delete params[key]; });
      const response = await getAuditLogs(params);
      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Error loading audit logs');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, filters]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await getUsers();
        setUsers(response.data);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ user_id: '', date_from: '', date_to: '' });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach((key) => { if (!params[key]) delete params[key]; });
      const response = await exportAuditLogs(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch (error) {
      toast.error('Error exporting audit logs');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters = Object.values(filters).some((v) => v);

  const getMethodBadge = (method) => {
    const config = {
      GET: { class: 'badge-draft', label: 'GET' },
      POST: { class: 'badge-approved', label: 'POST' },
      PUT: { class: 'badge-pending', label: 'PUT' },
      PATCH: { class: 'badge-pending', label: 'PATCH' },
      DELETE: { class: 'badge-rejected', label: 'DELETE' },
    };
    const c = config[method] || { class: 'badge-draft', label: method };
    return <span className={`badge ${c.class}`}>{c.label}</span>;
  };

  const getStatusBadge = (status) => {
    if (status >= 200 && status < 300) {
      return <span className="badge badge-approved">{status}</span>;
    } else if (status >= 400 && status < 500) {
      return <span className="badge badge-rework">{status}</span>;
    } else if (status >= 500) {
      return <span className="badge badge-rejected">{status}</span>;
    }
    return <span className="badge badge-draft">{status}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Logs</h1>
          <p className="page-subtitle">{pagination.total} log entries total</p>
        </div>
        <div className="flex gap-md">
          <button className="btn btn-secondary" onClick={handleExport} disabled={exporting}>
            <ArrowDownTrayIcon style={{ width: 18, height: 18 }} />
            {exporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="search-bar">
          <button
            className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon style={{ width: 18, height: 18 }} />
            Filters {hasActiveFilters && <span style={{ marginLeft: 4 }}>•</span>}
          </button>
        </div>

        {showFilters && (
          <div style={{
            marginBottom: 'var(--space-lg)',
            padding: 'var(--space-md)',
            background: 'var(--color-bg-tertiary)',
            borderRadius: 'var(--radius-md)'
          }}>
            <div className="filters-row" style={{ marginBottom: 'var(--space-md)' }}>
              <div className="filter-group">
                <label>User</label>
                <select
                  className="form-select"
                  value={filters.user_id}
                  onChange={(e) => handleFilterChange('user_id', e.target.value)}
                >
                  <option value="">All users</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.username} - {u.full_name}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Date From</label>
                <input
                  type="date"
                  className="form-input"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label>Date To</label>
                <input
                  type="date"
                  className="form-input"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                />
              </div>
            </div>
            {hasActiveFilters && (
              <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
                Clear all filters
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="loading-overlay">
            <div className="spinner" />
            <span>Loading audit logs...</span>
          </div>
        ) : logs.length > 0 ? (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Method</th>
                    <th>Path</th>
                    <th>IP Address</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ClockIcon style={{ width: 16, height: 16, opacity: 0.6 }} />
                          {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: 500 }}>{log.username}</div>
                          <div style={{ fontSize: 12, opacity: 0.7 }}>ID: {log.user_id}</div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: 500 }}>{log.action_description}</div>
                          {log.resource_type && (
                            <div style={{ fontSize: 12, opacity: 0.7 }}>
                              {log.resource_type} {log.resource_id ? `#${log.resource_id}` : ''}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{getMethodBadge(log.http_method)}</td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.request_path}
                      </td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{log.ip_address}</td>
                      <td>{getStatusBadge(log.response_status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <ClockIcon style={{ width: 48, height: 48, opacity: 0.3 }} />
            <h3>No audit logs found</h3>
            <p>No activity has been logged yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
